"use client";

/**
 * Logo component that displays enterprise, Northstar, or Nowadays logo
 * Uses enterprise context for logo data (populated server-side)
 * Supports PNG and SVG formats with fallback to Nowadays logo on error
 */

import { useEffect, useState } from "react";
import { useEnterprise } from "@/lib/enterprise/EnterpriseProvider";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { IS_NORTHSTAR } from "./utils";

/** Logo width presets (height auto-scales via intrinsic ratio) */
const LOGO_SIZES = {
  sm: { className: "w-24", width: 96 },
  md: { className: "w-32", width: 128 },
  lg: { className: "w-40", width: 160 },
} as const;

const DEFAULT_LOGO_RATIO = 2084 / 570; // public/nowadays_black.svg
const NORTHSTAR_LOGO_RATIO = 1898 / 674; // public/northstar-meetings-logo.png
const POWERED_BY_RATIO = 2025 / 529; // public/nowadays_northstar.svg
const POWERED_BY_SCALE = 120 / 180;

/** Available logo size presets */
type LogoSize = keyof typeof LOGO_SIZES;

interface LogoProps {
  className?: string;
  /** Size preset: sm (96px), md (128px), lg (160px) */
  size?: LogoSize;
  /** Layout for Northstar logo: row (horizontal) or column (stacked) */
  layout?: "row" | "column";
  /**
   * Hint the browser to preload this logo. Set only for above-the-fold
   * placements (e.g. a page header) — forwards `priority` to next/image and
   * `fetchPriority="high"` to the enterprise `<img>` fallback.
   */
  priority?: boolean;
}

const Logo = ({ className, size = "md", layout = "row", priority = false }: LogoProps) => {
  // Hooks
  const { logo: enterpriseLogo } = useEnterprise();

  // States
  const [logoError, setLogoError] = useState(false);

  // Derived values
  const sizeConfig = LOGO_SIZES[size];
  const imgClass = cn(sizeConfig.className, "h-auto object-contain");

  // Handlers
  const handleLogoError = () => setLogoError(true);

  // Effects
  useEffect(() => {
    setLogoError(false);
  }, [enterpriseLogo?.src]);

  // Render
  // Enterprise logos are external URLs - use img tag instead of next/image
  // to avoid requiring remotePatterns config for each enterprise domain
  // Supports both PNG and SVG formats; falls back to Nowadays logo on error
  if (enterpriseLogo && !logoError) {
    const aspectRatio = enterpriseLogo.width / enterpriseLogo.height;
    const height = Math.round(sizeConfig.width / aspectRatio);

    return (
      <div className={cn("flex items-center", className)}>
        <img
          src={enterpriseLogo.src}
          alt={enterpriseLogo.alt}
          width={sizeConfig.width}
          height={height}
          className={imgClass}
          onError={handleLogoError}
          fetchPriority={priority ? "high" : undefined}
          loading={priority ? "eager" : undefined}
        />
      </div>
    );
  }

  // Northstar layout
  if (IS_NORTHSTAR) {
    const northstarWidth = sizeConfig.width;
    const northstarHeight = Math.round(northstarWidth / NORTHSTAR_LOGO_RATIO);
    const poweredByWidth = Math.round(northstarWidth * POWERED_BY_SCALE);
    const poweredByHeight = Math.round(poweredByWidth / POWERED_BY_RATIO);

    const isColumn = layout === "column";

    return (
      <div className={cn("flex gap-2", isColumn ? "flex-col items-start" : "flex-row items-center", className)}>
        <Image
          src="/northstar-meetings-logo.png"
          alt="Northstar Logo"
          width={northstarWidth}
          height={northstarHeight}
          priority={priority}
          className={imgClass}
        />
        <div className="flex items-center gap-1.5">
          <span className="whitespace-nowrap text-sm font-medium text-gray-600">Powered by</span>
          <Image
            src="/nowadays_northstar.svg"
            alt="Nowadays Logo"
            width={poweredByWidth}
            height={poweredByHeight}
            unoptimized
            className="h-auto object-contain"
          />
        </div>
      </div>
    );
  }

  // Default Nowadays logo
  const defaultWidth = sizeConfig.width;
  const defaultHeight = Math.round(defaultWidth / DEFAULT_LOGO_RATIO);

  return (
    <div className={cn("flex items-center", className)}>
      <Image
        src="/nowadays_black.svg"
        alt="Nowadays Logo"
        width={defaultWidth}
        height={defaultHeight}
        unoptimized
        priority={priority}
        className={imgClass}
      />
    </div>
  );
};

export default Logo;
